import { useCallback } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HWA_LAUNCH_FLAG } from "~/features/resources/dimensions";
import { useFeatureFlag } from "~/hooks/use-account-feature-flags";
import { useUserDetails } from "~/hooks/queries/use-user-details";

/**
 * AIDEV-NOTE: Profile-scoped, same reason as `hwaAccessQueryKey` — the QueryClient outlives
 * an in-place session change, so an unscoped key would show the new viewer the previous
 * one's spent (or unspent) pip.
 */
const PROMO_SEEN_QUERY_KEY_ROOT = ["hwa-promo-seen"] as const;

function promoSeenQueryKey(profileId: string | undefined | null) {
    return [...PROMO_SEEN_QUERY_KEY_ROOT, profileId ?? null] as const;
}

/**
 * The orange pip on the Resources nav after the Academy launches, and the one-shot that
 * clears it.
 *
 * AIDEV-NOTE: Shows only while `hwa-launch` is on AND the viewer has not opened Resources
 * since launch. Every unknown state resolves to NOT showing: an in-flight read, a failed
 * revalidation, an unresolved viewer, and a flag-off environment all return false, because
 * a pip that appears late is forgettable while one that will not clear trains people to
 * ignore every badge we ship.
 */
export function useHwaPromoPip(): {
    showPip: boolean;
    markSeen: () => void;
} {
    const hwaReleased = useFeatureFlag(HWA_LAUNCH_FLAG);
    const queryClient = useQueryClient();
    // Same `["user"]` query the flag above reads — no extra request.
    const { data: userDetails } = useUserDetails();
    const profileId = userDetails?.profile?.id;
    const queryKey = promoSeenQueryKey(profileId);

    const { data, isFetching, isError } = useQuery<{ seen: boolean }>({
        queryKey,
        enabled: hwaReleased && !!profileId,
        queryFn: async () => {
            const res = await fetch("/api/hwa/promo-seen");
            if (!res.ok) return { seen: true };
            return (await res.json()) as { seen: boolean };
        },
        staleTime: 5 * 60 * 1000,
    });

    const { mutate } = useMutation({
        mutationFn: async () => {
            await fetch("/api/hwa/promo-seen", { method: "POST" });
        },
        // Clear the pip immediately; the viewer is looking at the page that spends it.
        onMutate: () => {
            queryClient.setQueryData(queryKey, { seen: true });
        },
    });

    // AIDEV-NOTE: `!isFetching && !isError` is what keeps a failed REVALIDATION from
    // leaving the pip lit. react-query keeps the last good `{seen:false}` as `data` when a
    // later refetch fails, so without these the badge would stay on with nothing able to
    // clear it.
    const showPip =
        hwaReleased &&
        !!profileId &&
        !isFetching &&
        !isError &&
        data?.seen === false;

    const markSeen = useCallback(() => {
        if (!hwaReleased || !profileId) return;
        // Already spent — do not POST again on every visit to Resources.
        if (data?.seen !== false) return;
        mutate();
    }, [hwaReleased, profileId, data?.seen, mutate]);

    return { showPip, markSeen };
}
